// Relative imports (not `@/`) so bun can test this module.

import { getHexTileMapKey } from '../hexmap/model/keys';
import type {
  BuildingDef,
  BuildingLevelDef,
  RuleSet,
  ShotDef,
  UnitDef,
  UnitUpgradeDef,
} from '../rules/schema';
import {
  type Building,
  buildingLevel,
  buildingMaxHp,
  buildingRange,
  buildingStrength,
  type CityBonus,
  canPromote,
  canUpgrade,
  capCount,
  capType,
  cityBonuses,
  type EntityId,
  extraTilesAllowed,
  fromMilli,
  holdsStock,
  levelDef,
  levelStat,
  levelTitle,
  maxLevel,
  type PlayerId,
  promoteCost,
  type SimContext,
  type SimState,
  territoryOwners,
  type Unit,
  type UnitType,
  unitCap,
} from '../sim';
import { upgradeCityLevel } from '../sim/levels';
import { UNDER_ATTACK_TICKS } from '../sim/placement';
import { cityName } from './cities';
import type { Names } from './names';
import { type PromotionOption, unitCountText, unitName } from './promotions';
import { cityUnlocks, unitCityLevel, upgradeRejectionText } from './reasons';
import type { Verb } from './verbs';

/**
 * What an upgrade does, ready to draw (pure, no React): a before → after row
 * per stat that changes, the requirements as met/unmet chips with progress,
 * what it enables, and which own units it affects. One shape for a city or
 * building level (`levelPreview`), a unit-type upgrade
 * (`unitUpgradePreview`) and a rank-up (built in `upgradesList.ts`), so the `UpgradeCard` draws them all alike.
 *
 * The level rows are generated from `LEVEL_FIELDS`, one entry per key of the
 * rules schema's level (a test fails when a key has no entry), so a stat a
 * level sets can't go missing from the card.
 */

export interface DiffRow {
  key: string;
  label: string;
  before: string;
  after: string;
  /** "+2", "+10%": null when the values aren't numbers. */
  delta: string | null;
  /** Whether the value rises, falls or (text) just changes. */
  trend: 'up' | 'down' | 'changed';
}

export interface Requirement {
  /** "City level 3/5", "Barracks 2/4", "Not under attack". */
  label: string;
  met: boolean;
}

/** Own units an upgrade touches, to list and to highlight. */
export interface AffectedGroup {
  label: string;
  ids: string[];
  /** A unit upgrade can't take these now (they sit outside the right city, say). */
  held?: boolean;
}

export interface UpgradePreview {
  kind: 'city' | 'building' | 'unit' | 'rank';
  /** The fixed verb (view/verbs.ts): `levelUp` for a building or city, `upgrade` for a unit type. */
  verb: Extract<Verb, 'levelUp' | 'upgrade' | 'rankUp'>;
  from: { name: string; tag: string };
  to: { name: string; tag: string };
  rows: DiffRow[];
  requirements: Requirement[];
  /** Units, buildings and unit-upgrade paths this opens, by name. */
  enables: string[];
  affects: AffectedGroup[];
  /** "3 ready · 2 outside a city with an Armoury" (unit upgrades), else null. */
  breakdown: string | null;
  /** What the action spends (for a unit upgrade: all ready units, else one). */
  cost: Record<string, number>;
  /** Units the action applies to now (1 for a level). */
  count: number;
  /** Whether the action can go ahead. */
  ok: boolean;
  /** Why not (null when `ok`). */
  reason: string | null;
}

// ── Formatting ───────────────────────────────────────────────────────────

const round = (n: number) => Number(n.toFixed(2));
const pct = (v: number) => `${round(v * 100)}%`;
const num = (v: number | string) => String(typeof v === 'number' ? round(v) : v);
const signed = (d: number) => `${d > 0 ? '+' : '−'}${round(Math.abs(d))}`;
const signedPct = (d: number) => `${d > 0 ? '+' : '−'}${pct(Math.abs(d))}`;

// ── Level fields ─────────────────────────────────────────────────────────

/** What a field's value needs to be read at a level. */
interface LevelCtx {
  state: SimState;
  rules: RuleSet;
  def: BuildingDef;
  building: Building;
  /**
   * The sim's city bonuses and the state with the building at `level`
   * (cached): hp, fire and city bonuses read as the sim will compute them
   * there, including barracks stacking limits.
   */
  at: (level: number) => { state: SimState; bonuses: Map<EntityId, CityBonus> };
  /** The building's city (itself, for a city), when the viewer owns one. */
  city: Building | undefined;
  /** "City 2": names the city a barracks bonus lands on. */
  cityLabel: string;
}

interface FieldSpec {
  label: string | ((c: LevelCtx) => string);
  /** The value at `level`; undefined = the level has none. */
  value: (c: LevelCtx, level: number) => number | string | undefined;
  format: (v: number | string) => string;
  /** How a change reads on its chip (default: signed number). */
  delta?: (d: number) => string;
}

const at = (c: LevelCtx, level: number): Building => ({ ...c.building, level });
/** `building` (or its city) as it stands in the state at `level`. */
const atLevel = (c: LevelCtx, level: number, b: Building): Building =>
  c.at(level).state.buildings[b.id] ?? b;
const stat = (c: LevelCtx, level: number, s: Parameters<typeof levelStat>[2]) =>
  levelStat(c.def, level, s);

/** The shot a building fires at `level`: its `shot`, else rules.yaml `shots` defaults. */
function shotAt(c: LevelCtx, level: number): string {
  if ((buildingRange(c.rules, at(c, level)) ?? 1) < 2) return 'continuous';
  let shot: ShotDef | undefined = c.def.shot;
  for (let l = Math.min(level, maxLevel(c.def)); l >= 2; l--) {
    const own = levelDef(c.def, l)?.shot;
    if (own) {
      shot = own;
      break;
    }
  }
  return `${shot?.kind ?? 'arrow'} · every ${shot?.fireInterval ?? c.rules.shots.fireInterval}s`;
}

/**
 * A bonus a building gives its city, as the city's final value at `level`
 * (the sim's own numbers with this building raised to it). Without a city of
 * the viewer's, the bonus alone ("+20").
 */
function cityFinal(
  c: LevelCtx,
  level: number,
  s: 'cityHp' | 'cityStrength' | 'cityVision',
  final: (city: Building, bonuses: Map<EntityId, CityBonus>, state: SimState) => number
): number | string | undefined {
  // A building that gives its city nothing (a city itself) has no such row.
  if (!stat(c, level, s) && !stat(c, buildingLevel(c.building), s)) return undefined;
  if (!c.city) return `+${stat(c, level, s) ?? 0}`;
  const { state, bonuses } = c.at(level);
  return final(state.buildings[c.city.id] ?? c.city, bonuses, state);
}

/**
 * One entry per level field of the rules schema (`BuildingLevelSchema`),
 * in the order rows are shown. `cost`, `title`, `cityLevel` and `upgradeTime` aren't
 * stats: the card shows them as cost, header, requirement and duration.
 */
export const LEVEL_FIELDS: Record<
  Exclude<keyof BuildingLevelDef, 'cost' | 'title' | 'cityLevel' | 'upgradeTime'>,
  FieldSpec
> = {
  hp: {
    label: 'Hit points',
    // With barracks bonuses and a last stand, like the panel header's chip.
    value: (c, l) =>
      Math.ceil(
        fromMilli(buildingMaxHp(c.at(l).state, c.rules, atLevel(c, l, c.building), c.at(l).bonuses))
      ),
    format: num,
  },
  strength: {
    label: 'Fire per second',
    value: (c, l) => buildingStrength(c.rules, atLevel(c, l, c.building), c.at(l).bonuses),
    format: (v) => `${num(v)}/s`,
  },
  range: {
    label: 'Fire reach',
    value: (c, l) => buildingRange(c.rules, at(c, l)),
    format: (v) => `${num(v)} ${v === 1 ? 'tile' : 'tiles'}`,
  },
  shot: { label: 'Shots', value: shotAt, format: String },
  territoryRadius: {
    label: 'Territory',
    value: (c, l) => stat(c, l, 'territoryRadius'),
    format: (v) => `${num(v)} ${v === 1 ? 'tile' : 'tiles'}`,
  },
  visionRadius: {
    label: 'Sight',
    value: (c, l) => stat(c, l, 'visionRadius'),
    format: (v) => `${num(v)} ${v === 1 ? 'tile' : 'tiles'}`,
  },
  gatherBonus: {
    label: 'Gathering',
    value: (c, l) => stat(c, l, 'gatherBonus') ?? 0,
    format: (v) => `+${pct(Number(v))}`,
    delta: signedPct,
  },
  rate: {
    label: 'Gather rate',
    value: (c, l) => stat(c, l, 'rate'),
    format: (v) => `${num(v)}×`,
    delta: (d) => `${signed(d)}×`,
  },
  workerSlots: {
    label: 'Worker slots',
    value: (c, l) => stat(c, l, 'workerSlots') ?? 1,
    format: num,
  },
  capacity: {
    label: 'Stores',
    value: (c, l) => stat(c, l, 'capacity') ?? 0,
    format: (v) => `${num(v)} units`,
  },
  trainSlots: {
    label: 'Trains at once',
    value: (c, l) => stat(c, l, 'trainSlots'),
    format: num,
  },
  cityHp: {
    label: (c) => `${c.cityLabel} hp`,
    value: (c, l) =>
      cityFinal(c, l, 'cityHp', (city, bonuses, state) =>
        Math.ceil(fromMilli(buildingMaxHp(state, c.rules, city, bonuses)))
      ),
    format: num,
  },
  cityStrength: {
    label: (c) => `${c.cityLabel} fire`,
    value: (c, l) =>
      cityFinal(c, l, 'cityStrength', (city, bonuses) => buildingStrength(c.rules, city, bonuses)),
    format: (v) => (typeof v === 'number' ? `${num(v)}/s` : `${v}/s`),
  },
  cityVision: {
    label: (c) => `${c.cityLabel} sight`,
    value: (c, l) =>
      cityFinal(c, l, 'cityVision', (city, bonuses) => {
        const cityDef = c.rules.buildings[city.type];
        const radius = cityDef ? (levelStat(cityDef, buildingLevel(city), 'visionRadius') ?? 0) : 0;
        return radius + (bonuses.get(city.id)?.vision ?? 0);
      }),
    format: num,
  },
  watchRange: {
    label: 'Watch reach',
    value: (c, l) => stat(c, l, 'watchRange'),
    format: (v) => `${num(v)} tiles`,
  },
  watchRadius: {
    label: 'Sees round its spot',
    value: (c, l) => stat(c, l, 'watchRadius'),
    format: (v) => `${num(v)} tiles`,
  },
  repair: {
    label: 'Repairs',
    // Mending after a fight (pacing.repairDelay): a level may set its own share.
    value: (c, l) =>
      stat(c, l, 'repair') ??
      (holdsStock(c.rules, c.building) ? c.rules.pacing.cityRepair : c.rules.pacing.buildingRepair),
    format: (v) => `${pct(Number(v))}/s`,
    delta: (d) => `${signedPct(d)}/s`,
  },
};

/** The level keys that are not stats (see `LEVEL_FIELDS`). */
export const LEVEL_NON_STATS = ['cost', 'title', 'cityLevel', 'upgradeTime'] as const;

function diffRow(
  key: string,
  label: string,
  spec: Pick<FieldSpec, 'format' | 'delta'>,
  a: unknown,
  b: unknown
) {
  if (a === b || b === undefined) return null;
  const before = a === undefined ? '—' : spec.format(a as number | string);
  const after = spec.format(b as number | string);
  if (before === after) return null;
  if (typeof a === 'number' && typeof b === 'number') {
    const d = b - a;
    return {
      key,
      label,
      before,
      after,
      delta: (spec.delta ?? signed)(d),
      trend: d > 0 ? 'up' : 'down',
    } satisfies DiffRow;
  }
  return { key, label, before, after, delta: null, trend: 'changed' } satisfies DiffRow;
}

/** The own completed city whose territory holds `b` (b itself for a city). */
function cityOf(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  b: Building,
  owners: ReadonlyMap<string, EntityId>
): Building | undefined {
  const city = holdsStock(rules, b)
    ? b
    : state.buildings[owners.get(getHexTileMapKey(b.pos)) ?? ''];
  return city?.owner === playerId ? city : undefined;
}

/**
 * The preview of raising `building` (a city or any levelled building) one
 * level; null at max level. `cityNumbers` names the city a refusal waits on
 * ("City 2 …", or its name from `names`). Rows, requirements and enables cover everything the level
 * changes, from the schema-driven `LEVEL_FIELDS`.
 */
export function levelPreview(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  building: Building,
  cityNumbers: ReadonlyMap<string, number> = new Map(),
  names?: Names
): UpgradePreview | null {
  const { rules } = ctx;
  const def = rules.buildings[building.type];
  if (!def?.levels) return null;
  const level = buildingLevel(building);
  const next = levelDef(def, level + 1);
  if (!next) return null;
  const owners = territoryOwners(state, rules);
  const city = cityOf(state, rules, playerId, building, owners);
  const name = (c: Building) => cityName(c, playerId, cityNumbers, undefined, names);
  const cache = new Map<number, ReturnType<LevelCtx['at']>>();
  const lc: LevelCtx = {
    state,
    rules,
    def,
    building,
    at: (l) => {
      let hit = cache.get(l);
      if (!hit) {
        // Territory (`owners`) is kept as now: a city level that grows its
        // border isn't re-partitioned here, which only moves bonus edge cases.
        const raised = {
          ...state,
          buildings: { ...state.buildings, [building.id]: { ...building, level: l } },
        };
        hit = { state: raised, bonuses: cityBonuses(raised, rules, owners) };
        cache.set(l, hit);
      }
      return hit;
    },
    city,
    cityLabel: city ? name(city) : 'City',
  };

  const rows: DiffRow[] = [];
  for (const [key, spec] of Object.entries(LEVEL_FIELDS)) {
    const label = typeof spec.label === 'string' ? spec.label : spec.label(lc);
    const row = diffRow(key, label, spec, spec.value(lc, level), spec.value(lc, level + 1));
    if (row) rows.push(row);
  }
  // Derived from the level, not set by a schema key.
  if (def.trainSlots !== undefined) {
    const cap = (l: number) => Math.min(l, rules.veterancy.ranks.length);
    const row = diffRow(
      'rankCap',
      'Trains up to',
      { format: (v) => `rank ${v}` },
      cap(level),
      cap(level + 1)
    );
    if (row) rows.push(row);
  }
  if (def.growth) {
    const row = diffRow(
      'extraTiles',
      'Extra tiles',
      { format: num },
      extraTilesAllowed(def, level),
      extraTilesAllowed(def, level + 1)
    );
    if (row) rows.push(row);
  }
  for (const type of def.spawns ?? []) {
    const perLevel = rules.units[type]?.perCityLevel ?? 0;
    if (perLevel <= 0) continue;
    const before = unitCap(state, rules, playerId, type);
    const row = diffRow(
      `cap:${type}`,
      `${rules.units[type]?.name ?? type} cap`,
      { format: num },
      before,
      before + perLevel
    );
    if (row) rows.push(row);
  }

  // Requirements, with progress.
  const check = canUpgrade(state, ctx, playerId, building.id, owners);
  const requirements: Requirement[] = [];
  const needCity = upgradeCityLevel(def, level + 1);
  if (needCity > 0) {
    const have = city ? buildingLevel(city) : 0;
    requirements.push({ label: `City level ${have}/${needCity}`, met: have >= needCity });
  }
  requirements.push({
    label: 'Not under attack',
    met:
      building.lastDamagedTick === undefined ||
      state.tick - building.lastDamagedTick >= UNDER_ATTACK_TICKS,
  });

  // What it opens: units, buildings and levels, unit-upgrade paths, ranks.
  const next1 = level + 1;
  const enables = new Set<string>();
  if (def.territoryRadius !== undefined) for (const u of cityUnlocks(rules, next1)) enables.add(u);
  const upgradePaths: { from: UnitType; to: UnitType; counts: boolean }[] = [];
  for (const [type, u] of Object.entries(rules.units)) {
    if (u.requires?.building === building.type && u.requires.level === next1) enables.add(u.name);
    for (const up of u.upgrades ?? []) {
      const viaBuilding = up.at?.building === building.type && up.at.level === next1;
      // A city era makes the target type appear, but the sim only gates a
      // promotion on `at`: an era path with an `at` isn't opened by this level.
      const viaEra =
        !up.at &&
        def.territoryRadius !== undefined &&
        next1 > 1 &&
        unitCityLevel(rules, up.to) === next1;
      if (!viaBuilding && !viaEra) continue;
      upgradePaths.push({ from: type, to: up.to, counts: viaBuilding });
      enables.add(`${u.name} → ${rules.units[up.to]?.name ?? up.to}`);
    }
  }
  if (def.trainSlots !== undefined && next1 <= rules.veterancy.ranks.length) {
    enables.add(`Rank ${next1} training`);
  }
  if (def.growth && extraTilesAllowed(def, next1) > extraTilesAllowed(def, level)) {
    const { slotsPerExtraTile: workers, capacityPerExtraTile: stored } = def.growth;
    const each = workers
      ? ` (+${workers} workers each)`
      : stored
        ? ` (+${stored} stored each)`
        : '';
    enables.add(`Room for more tiles${each}`);
  }

  // Own units it touches: ones inside the building, and ones that could take a path it opens.
  const affects: AffectedGroup[] = [];
  const mine = Object.values(state.units).filter((u) => u.owner === playerId);
  const inside = mine.filter((u) => u.inside === building.id);
  if (inside.length > 0) affects.push(...groupByType(rules, inside, (n) => `${n} inside`));
  for (const type of new Set(upgradePaths.filter((p) => p.counts).map((p) => p.from))) {
    const ids = mine.filter((u) => u.type === type).map((u) => u.id);
    if (ids.length > 0) {
      affects.push({ label: `${unitCountText(rules, type, ids.length)} could upgrade`, ids });
    }
  }

  const reason = check.ok
    ? null
    : (upgradeRejectionText(state, rules, building, playerId, check, name) ?? 'Not available');
  return {
    kind: holdsStock(rules, building) ? 'city' : 'building',
    verb: 'levelUp',
    from: { name: levelTitle(def, level), tag: `L${level}` },
    to: { name: levelTitle(def, next1), tag: `L${next1}/${maxLevel(def)}` },
    rows,
    requirements,
    enables: [...enables],
    affects,
    breakdown: null,
    cost: next.cost,
    count: 1,
    ok: check.ok,
    reason,
  };
}

/** "2 Archers inside"-style groups, one per unit type. */
function groupByType(
  rules: RuleSet,
  units: readonly Unit[],
  text: (countText: string) => string
): AffectedGroup[] {
  const byType = new Map<UnitType, string[]>();
  for (const u of units) byType.set(u.type, [...(byType.get(u.type) ?? []), u.id]);
  return [...byType].map(([type, ids]) => ({
    label: text(unitCountText(rules, type, ids.length)),
    ids,
  }));
}

// ── Unit-type upgrades ───────────────────────────────────────────────────

interface UnitFieldSpec {
  label: string;
  value: (def: UnitDef, rules: RuleSet) => number | string | undefined;
  format: (v: number | string) => string;
  delta?: (d: number) => string;
}

/** Every damage multiplier as "×2 vs Cavalry, ×1.5 vs Ram". */
const bonusVsText = (def: UnitDef, rules: RuleSet) =>
  Object.entries(def.bonusVs ?? {})
    .map(([type, m]) => `×${m} vs ${rules.units[type]?.name ?? type}`)
    .join(', ') || undefined;

/** The building that stores (and so trains) a military unit type, by name. */
function storeName(rules: RuleSet, type: UnitType): string | undefined {
  const def = rules.units[type];
  if (!def?.military) return undefined;
  const stores = Object.values(rules.buildings).filter((b) => b.capacity);
  return (stores.find((b) => b.stores?.includes(type)) ?? stores.find((b) => !b.stores))?.name;
}

/**
 * How each key of a unit def shows in a unit-type upgrade; `null` = not a
 * stat the card compares (identity, costs, rules the sim reads). A test
 * checks every schema key is listed.
 */
export const UNIT_FIELDS: Record<keyof UnitDef, UnitFieldSpec | null> = {
  hp: { label: 'Hit points', value: (d) => d.hp, format: num },
  strength: { label: 'Damage per second', value: (d) => d.strength, format: (v) => `${num(v)}/s` },
  range: {
    label: 'Reach',
    value: (d) => d.range,
    format: (v) => `${num(v)} ${v === 1 ? 'tile' : 'tiles'}`,
  },
  speedMultiplier: {
    label: 'Speed',
    value: (d) => d.speedMultiplier,
    format: (v) => `${num(v)}×`,
    delta: (d) => `${signed(d)}×`,
  },
  visionRadius: { label: 'Sight', value: (d) => d.visionRadius, format: (v) => `${num(v)} tiles` },
  healRate: { label: 'Heals', value: (d) => d.healRate, format: (v) => `${num(v)} hp/s` },
  shot: {
    label: 'Shots',
    value: (d, r) =>
      d.shot ? `${d.shot.kind} · every ${d.shot.fireInterval ?? r.shots.fireInterval}s` : undefined,
    format: String,
  },
  carryCapacity: { label: 'Carries', value: (d) => d.carryCapacity, format: num },
  gatherMultiplier: {
    label: 'Gathering',
    value: (d) => d.gatherMultiplier ?? 1,
    format: (v) => `${num(v)}×`,
    delta: (d) => `${signed(d)}×`,
  },
  bonusVs: { label: 'Strong against', value: (d, r) => bonusVsText(d, r), format: String },
  bonusVsBuildings: {
    label: 'Vs buildings',
    value: (d) => d.bonusVsBuildings ?? 1,
    format: (v) => `×${num(v)}`,
    delta: (d) => `${signed(d)}×`,
  },
  targets: { label: 'Targets', value: (d) => d.targets, format: String },
  // Upkeep and the cap are per resource / per player: see `unitUpgradePreview`.
  upkeep: null,
  perCity: null,
  perCityLevel: null,
  capAs: null,
  name: null,
  ui: null,
  cost: null,
  terrain: null,
  actions: null,
  heals: null,
  mechanical: null,
  military: null,
  trainTime: null,
  automate: null,
  costPerCity: null,
  requires: null,
  upgrades: null,
  stealth: null,
  disguiseAs: null,
};

/** "Barracks" → "a Barracks", "Armoury" → "an Armoury". */
const withArticle = (name: string) => `${/^[aeiou]/i.test(name) ? 'an' : 'a'} ${name}`;

/**
 * The preview of promoting a unit type into another (`option`: from
 * `typePromotions`/`promotionOptions`). Rows compare the two types' stats;
 * `affects` splits the eligible units into those ready now and the held-back
 * ones by why ("outside a city with an Armoury").
 */
export function unitUpgradePreview(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  from: UnitType,
  option: PromotionOption,
  real: SimState = state,
  cityNumbers: ReadonlyMap<string, number> = new Map(),
  names?: Names
): UpgradePreview {
  const { rules } = ctx;
  const a = rules.units[from];
  const b = rules.units[option.to];
  const rows: DiffRow[] = [];
  if (a && b) {
    for (const [key, spec] of Object.entries(UNIT_FIELDS)) {
      if (!spec) continue;
      const row = diffRow(key, spec.label, spec, spec.value(a, rules), spec.value(b, rules));
      if (row) rows.push(row);
    }
    // Upkeep: per second, per resource.
    for (const res of new Set([...Object.keys(a.upkeep ?? {}), ...Object.keys(b.upkeep ?? {})])) {
      const row = diffRow(
        `upkeep:${res}`,
        `Upkeep ${res}`,
        { format: (v) => `${num(v)}/s`, delta: (d) => `${signed(d)}/s` },
        a.upkeep?.[res] ?? 0,
        b.upkeep?.[res] ?? 0
      );
      if (row) rows.push(row);
    }
    const capA = unitCap(real, rules, playerId, from);
    const capB = unitCap(real, rules, playerId, option.to);
    if (capA !== capB && Number.isFinite(capB)) {
      const row = diffRow(
        'cap',
        'Cap',
        { format: (v) => (v === Number.POSITIVE_INFINITY ? 'none' : num(v)) },
        capA,
        capB
      );
      if (row) rows.push(row);
    }
    const storeA = storeName(rules, from);
    const storeB = storeName(rules, option.to);
    if (storeA !== storeB && storeB) {
      rows.push({
        key: 'storage',
        label: 'Stored in',
        before: storeA ?? 'nowhere',
        after: storeB,
        delta: null,
        trend: 'changed',
      });
    }
  }

  // Requirement: the building the upgrade needs, in the city of the first
  // unit standing in one (the sim checks it per city). The new type's era
  // isn't a requirement: the sim only gates on `at`.
  const owners = territoryOwners(state, rules);
  const upgrade: UnitUpgradeDef | undefined = a?.upgrades?.find((u) => u.to === option.to);
  const requirements: Requirement[] = [];
  if (upgrade?.at) {
    const { building: type, level: need } = upgrade.at;
    const bname = rules.buildings[type]?.name ?? type;
    const city = option.eligible
      .map((u) => state.buildings[owners.get(getHexTileMapKey(u.pos)) ?? ''])
      .find((c) => c && c.owner === playerId && holdsStock(rules, c));
    if (city) {
      let have = 0;
      for (const b of Object.values(state.buildings)) {
        if (
          b.owner === playerId &&
          b.type === type &&
          b.buildProgress >= 1 &&
          owners.get(getHexTileMapKey(b.pos)) === city.id
        ) {
          have = Math.max(have, buildingLevel(b));
        }
      }
      requirements.push({
        label: `${bname} ${have}/${need} in ${cityName(city, playerId, cityNumbers, undefined, names)}`,
        met: have >= need,
      });
    } else {
      requirements.push({ label: `In a city with ${bname} ${need}`, met: false });
    }
  }

  // Further paths from the new type.
  const enables = (b?.upgrades ?? []).map(
    (up) => `${b?.name ?? option.to} → ${rules.units[up.to]?.name ?? up.to}`
  );

  // Ready vs held back, held back grouped by why.
  const readyIds = new Set(option.ready.map((u) => u.id));
  const heldBy = new Map<string, string[]>();
  for (const u of option.eligible) {
    if (readyIds.has(u.id)) continue;
    // Per unit, from the sim's own check; units it passes are held back by
    // the stock or cap as the ready ones spent them.
    const check = canPromote(state, ctx, u, option.to, owners);
    const capFull =
      capType(rules, u.type) !== capType(rules, option.to) &&
      unitCap(real, rules, playerId, option.to) -
        capCount(real, rules, playerId, option.to) -
        option.ready.length <=
        0;
    let why = capFull ? 'over the cap' : 'short of resources';
    if (!check.ok && check.reason === 'where') {
      const at = upgrade?.at;
      const bdef = at && rules.buildings[at.building];
      why = bdef
        ? `outside a city with ${withArticle(levelTitle(bdef, at.level))}`
        : 'outside your cities';
    } else if (!check.ok && check.reason === 'cap') why = 'over the cap';
    else if (!check.ok && check.reason === 'cost') why = 'short of resources';
    heldBy.set(why, [...(heldBy.get(why) ?? []), u.id]);
  }
  const affects: AffectedGroup[] = [];
  if (option.ready.length > 0) {
    affects.push({ label: `${option.ready.length} ready`, ids: option.ready.map((u) => u.id) });
  }
  for (const [why, ids] of heldBy) affects.push({ label: `${ids.length} ${why}`, ids, held: true });
  const breakdown = affects.length > 0 ? affects.map((g) => g.label).join(' · ') : null;

  const each = promoteCost(rules, from, option.to) ?? {};
  const ok = option.ready.length > 0;
  return {
    kind: 'unit',
    verb: 'upgrade',
    from: { name: unitName(rules, from, 1), tag: '' },
    to: { name: unitName(rules, option.to, 1), tag: '' },
    rows,
    requirements,
    enables,
    affects,
    breakdown,
    cost: ok ? option.cost : each,
    count: option.ready.length,
    ok,
    reason: ok
      ? null
      : (option.reason ??
        (option.eligible.length === 0 ? `No ${unitName(rules, from, 2)}` : 'Not now')),
  };
}
